import { isTaakAfgerond } from './parts/projectMeta';
import { useState, useMemo, useEffect } from 'react';
import { buildGrippProjectUrl, buildGrippOfferUrl } from '../../utils/grippDeepLink';
import { registreerOngedaanSneltoets } from '../../utils/ongedaan';
import {
  Box,
  Typography,
  LinearProgress,
  Drawer,
} from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import TaskTemplateGenerator from './TaskTemplateGenerator';
import SmartTaskGenerator from './SmartTaskGenerator';
import PreproductionWizard from './PreproductionWizard';
import WorkflowTemplateManager from './WorkflowTemplateManager';
import ProjectEditDialog from './ProjectEditDialog';


// Moment still used for date formatting
import moment from 'moment';
import 'moment/locale/nl';

import WijzigingslogKnop from '../../ui-components/WijzigingslogKnop';
import { T, Icon, stitchPill, stitchSectionLabel, stitchGradientBtn, stitchOutlineBtn, stitchSkeleton, StitchIconBtn } from './parts/stitch';

import { typeColors, statusColors } from './parts/projectMeta';
import TeamRegel from './parts/TeamRegel';
import TabPanel from './parts/TabPanel';
import DeliverableDetailDialog from './parts/DeliverableDetailDialog';
import TaskDetailDialog from './parts/TaskDetailDialog';
import PlanningEmailDialog from './parts/PlanningEmailDialog';
import { ProjectDataProvider, useProjectData } from './ProjectDataContext';
import { BACKEND_URL, AI_ENGINE_URL } from '../../utils/apiConfig';
import { authFetch } from '../../utils/authHelpers';
import ProjectFlowPage from '../ProjectFlow/ProjectFlowPage';
import { PANEL_LABELS } from '../../config/projectFlow';
import ActivityPanel from './panels/ActivityPanel';
import TasksPanel from './panels/TasksPanel';
import DeliverablesPanel from './panels/DeliverablesPanel';
import DraaidagenPanel from './panels/DraaidagenPanel';
import QuitclaimsPanel from './panels/QuitclaimsPanel';
import CallsheetsPanel from './panels/CallsheetsPanel';
import NotesPanel from './panels/NotesPanel';
import MeetingsPanel from './panels/MeetingsPanel';
import DocumentsPanel from './panels/DocumentsPanel';
import FrameioPanel from './panels/FrameioPanel';
import QaPanel from './panels/QaPanel';
import CorrespondencePanel from './panels/CorrespondencePanel';
import OfferteregelsPanel from './panels/OfferteregelsPanel';
import ScriptPanel from './panels/ScriptPanel';
import KlantVereistenBlok from '../../ui-components/KlantVereistenBlok';

moment.locale('nl');

// Main Component: de provider laadt en beheert alle projectdata (zie
// ProjectDataContext.jsx); de content-component rendert de tabbladen en houdt
// alleen nog UI-state (actieve tab, weergaves, dialogs).
function ProjectOverview() {
  return (
    <ProjectDataProvider>
      <ProjectOverviewContent />
    </ProjectDataProvider>
  );
}

// Tellers in de projectkop: heel subtiel klikbaar — opent het bijbehorende paneel.
const statClickSx = {
  minWidth: 80, cursor: 'pointer', borderRadius: '0.5rem', px: 0.75, mx: -0.75,
  transition: 'background-color 0.15s ease',
  '&:hover': { bgcolor: T.surfaceLow },
};

function ProjectOverviewContent() {
  const navigate = useNavigate();
  const { instance, accounts } = useMsal();
  const {
    id,
    loading,
    error,
    loadData,
    project,
    setProject,
    tasks,
    deliverables,
    setDeliverables,
    notes,
    transcripts,
    quitclaims,
    draaidagen,
    callsheets,
    frameioVideos,
    qaReviews,
    personen,
    quoteLines,
    pendingProposal,
    markProposal,
    selectedTask,
    setSelectedTask,
    autoEditTaskDate,
    setAutoEditTaskDate,
    handleTaskUpdate,
    handleTaskDelete,
    tasksPerDeliverable,
  } = useProjectData();

  // Ctrl/Cmd+Z = laatste afvink-/verplaats-actie terugdraaien (zie utils/ongedaan).
  useEffect(() => registreerOngedaanSneltoets(), []);

  // Waarnemend PL (actieve overdracht): niet de PL maar de waarnemer is nu
  // het aanspreekpunt — toon dat als chip in de header.
  const [waarneming, setWaarneming] = useState(null);
  useEffect(() => {
    if (!id) return;
    let actueel = true;
    authFetch(`${BACKEND_URL}/Api/overdracht/actief?project=${encodeURIComponent(id)}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (actueel) setWaarneming(d?.overdracht || null); })
      .catch(() => {});
    return () => { actueel = false; };
  }, [id]);

  // Subtiele documenten-teller op de stat-tegel (het paneel laadt de rest).
  const [docCount, setDocCount] = useState(null);
  useEffect(() => {
    if (!id) return;
    let actueel = true;
    authFetch(`${BACKEND_URL}/Api/project-flow/${id}/documents`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (actueel && d) setDocCount((d.documenten || []).length); })
      .catch(() => {});
    return () => { actueel = false; };
  }, [id]);
  // Correspondentie-teller op de stat-tegel (Joris 18 sep): zelfde bron als de tab,
  // 180 dagen, alleen het aantal — de tab laadt de inhoud pas bij openen.
  const [mailCount, setMailCount] = useState(null);
  useEffect(() => {
    if (!project) return;
    let actueel = true;
    const params = new URLSearchParams();
    const pn = project?.fields?.Projectnummer || project?.fields?.Projectnr || '';
    const naam = project?.fields?.Projectnaam || '';
    const em = project?.fields?.emailCP || [];
    const contact = Array.isArray(em) ? (em[0] || '') : String(em || '');
    if (pn) params.set('projectNumber', String(pn));
    if (naam) params.set('projectName', naam);
    if (contact) params.set('contactEmail', contact);
    params.set('days', '180');
    if (!pn && !naam && !contact) return undefined;
    authFetch(`${BACKEND_URL}/Api/mail-assistant/project-correspondence?${params}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (actueel && d) setMailCount((d.emails || []).length); })
      .catch(() => {});
    return () => { actueel = false; };
  }, [project]);
  // Weergave: de flow is de startpagina; ?weergave=tabs geeft de oude
  // tabbladen terug (ontsnappingsluik) en ?paneel=<key> opent elk paneel als
  // drawer — geen enkel tabblad wordt onbereikbaar.
  const [searchParams, setSearchParams] = useSearchParams();
  // De keuze wordt onthouden (localStorage) zodat wie liever de tabbladen heeft
  // niet bij elk project opnieuw hoeft te schakelen; een expliciete
  // ?weergave=-parameter (deeplink) wint altijd.
  const [weergaveVoorkeur, setWeergaveVoorkeur] = useState(() => {
    try { return localStorage.getItem('projectWeergave') === 'tabs' ? 'tabs' : 'flow'; }
    catch { return 'flow'; }
  });
  const urlWeergave = searchParams.get('weergave');
  const weergave = urlWeergave === 'tabs' || urlWeergave === 'flow' ? urlWeergave : weergaveVoorkeur;
  const kiesWeergave = (w) => {
    setWeergaveVoorkeur(w);
    try { localStorage.setItem('projectWeergave', w); } catch { /* prima */ }
    setParam('weergave', w === 'flow' ? null : w);
  };
  const paneel = searchParams.get('paneel');
  const setParam = (key, value) => {
    const next = new URLSearchParams(searchParams);
    if (value === null) next.delete(key); else next.set(key, value);
    setSearchParams(next, { replace: false });
  };
  const openPaneel = (key) => setParam('paneel', key);
  const closePaneel = () => setParam('paneel', null);

  // Paneel-render voor de drawer — zelfde componenten en props als de tabs.
  const renderPaneel = (key) => {
    switch (key) {
      case 'activiteit': return <ActivityPanel onOpenTab={openPaneel} />;
      case 'taken': return (
        <TasksPanel tasksView={tasksView} setTasksView={setTasksView}
          kanbanShowPLOnly={kanbanShowPLOnly} setKanbanShowPLOnly={setKanbanShowPLOnly}
          onOpenSmartTasks={() => setShowSmartTasks(true)}
          onOpenPreproWizard={() => setShowPreproWizard(true)} />
      );
      case 'deliverables': return (
        <DeliverablesPanel
          onOpenDetail={(item) => { setSelectedDeliverable(item); setShowDeliverableDetail(true); }}
          onGenerateTasks={(d) => { setSelectedDeliverable(d); setShowTaskGenerator(true); }} />
      );
      case 'draaidagen': return <DraaidagenPanel draaidagenView={draaidagenView} setDraaidagenView={setDraaidagenView} />;
      case 'quitclaims': return <QuitclaimsPanel />;
      case 'callsheets': return <CallsheetsPanel />;
      case 'notes': return <NotesPanel />;
      case 'meetings': return <MeetingsPanel />;
      case 'documenten': return <DocumentsPanel />;
      case 'frameio': return <FrameioPanel />;
      case 'qa': return <QaPanel />;
      case 'correspondentie': return <CorrespondencePanel active />;
      case 'offerteregels': return <OfferteregelsPanel />;
      case 'script': return <ScriptPanel />;
      default: return null;
    }
  };
  const [activeTab, setActiveTab] = useState('activiteit');
  const [draaidagenView, setDraaidagenView] = useState('list'); // 'calendar' or 'list'
  const [tasksView, setTasksView] = useState('phase'); // 'phase', 'grouped', 'list', 'kanban'
  const [kanbanShowPLOnly, setKanbanShowPLOnly] = useState(false); // Filter Kanban to show only PL tasks
  const [selectedDeliverable, setSelectedDeliverable] = useState(null); // For task generation
  const [showTaskGenerator, setShowTaskGenerator] = useState(false);
  const [showSmartTasks, setShowSmartTasks] = useState(false);
  const [showPreproWizard, setShowPreproWizard] = useState(false);
  const [showTemplateManager, setShowTemplateManager] = useState(false);
  const [showDeliverableDetail, setShowDeliverableDetail] = useState(false); // For deliverable detail dialog
  const [showPlanningEmail, setShowPlanningEmail] = useState(false);
  const [showProjectEdit, setShowProjectEdit] = useState(false); // For project edit dialog

  // Project info
  const title = project?.fields?.Projectnr_en_naam || project?.fields?.Projectnaam || 'Project';
  const status = project?.fields?.Status || 'In progress';
  const type = project?.fields?.Projecttype || '';
  // Use lookup fields for actual names, fallback to raw field
  const clientNames = project?.fields?.KlantnaamString || project?.fields?.Klantnaam || [];
  const client = Array.isArray(clientNames) ? clientNames[0] : clientNames || '';
  // Team: zie parts/TeamRegel (rollen + namen; editor heeft in Airtable geen naam-lookup).
  const statusStyle = statusColors[status] || statusColors['In progress'];

  // Stats
  const completedTasks = tasks.filter(isTaakAfgerond).length;
  const taskProgress = tasks.length > 0 ? (completedTasks / tasks.length) * 100 : 0;

  // Tab definitions
  const tabDefs = useMemo(() => {
    const tabs = [
      { key: 'activiteit', icon: 'dynamic_feed', label: 'Activiteit' },
      { key: 'taken', icon: 'task_alt', label: `Taken (${tasks.length})` },
      { key: 'deliverables', icon: 'movie', label: `Deliverables (${deliverables.length})` },
      { key: 'draaidagen', icon: 'event', label: `Draaidagen (${draaidagen.length})` },
      { key: 'quitclaims', icon: 'description', label: `Quitclaims (${quitclaims.length})` },
      { key: 'callsheets', icon: 'phone', label: `Callsheets (${callsheets.length})` },
      { key: 'notes', icon: 'notes', label: `Notes (${notes.length})` },
      { key: 'meetings', icon: 'mic', label: `Meetings (${transcripts.length})` },
    ];
    if (frameioVideos.length > 0) {
      tabs.push({ key: 'frameio', icon: 'videocam', label: `Frame.io (${frameioVideos.length})` });
    }
    tabs.push({ key: 'qa', icon: 'bug_report', label: `QA Reviews${qaReviews.length ? ` (${qaReviews.length})` : ''}` });
    tabs.push({ key: 'correspondentie', icon: 'forum', label: 'Correspondentie' });
    tabs.push({ key: 'offerteregels', icon: 'request_quote', label: `Offerteregels${quoteLines?.count ? ` (${quoteLines.count})` : ''}` });
    tabs.push({ key: 'script', icon: 'description', label: 'Script' });
    return tabs;
  }, [tasks.length, deliverables.length, draaidagen.length, quitclaims.length, callsheets.length, notes.length, transcripts.length, frameioVideos.length, qaReviews.length, quoteLines?.count]);

  if (loading) {
    return (
      <Box sx={{ width: '100%', maxWidth: 1800, mx: 'auto', px: { xs: 2, sm: 3, md: 5 }, pt: { xs: 3, md: 5 }, pb: 10 }}>
        <Box sx={stitchSkeleton(300, 40)} />
        <Box sx={{ ...stitchSkeleton(200, 24), mt: 1, mb: 3 }} />
        <Box sx={stitchSkeleton('100%', 200)} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ width: '100%', maxWidth: 1800, mx: 'auto', px: { xs: 2, sm: 3, md: 5 }, pt: { xs: 3, md: 5 }, pb: 10, textAlign: 'center' }}>
        <Typography sx={{ fontFamily: T.fontBody, color: T.error, mb: 2 }}>{error}</Typography>
        <Typography
          variant="body2"
          sx={{ fontFamily: T.fontBody, color: T.primary, cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
          onClick={loadData}
        >
          Opnieuw proberen
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', maxWidth: 1800, mx: 'auto', px: { xs: 2, sm: 3, md: 5 }, pt: { xs: 3, md: 5 }, pb: 10 }}>
      {/* Header — bewust compact: de flow eronder is het echte werkvlak */}
      <Box sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
          <StitchIconBtn onClick={() => navigate('/projects')} sx={{ bgcolor: T.surfaceLow }}>
            <Icon name="arrow_back" size={20} />
          </StitchIconBtn>
          <Typography component="span" sx={stitchPill(statusStyle.text, statusStyle.bg)}>
            {status}
          </Typography>
          {waarneming && (
            <Typography component="span" title={`Overdracht van ${waarneming.van}`}
              sx={stitchPill('#9a3412', '#ffedd5')}>
              🔁 Waargenomen door {waarneming.naar}{waarneming.tot ? ` · t/m ${new Date(waarneming.tot).toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' })}` : ''}
            </Typography>
          )}
          {type && (
            <Typography component="span" sx={{
              ...stitchPill(typeColors[type] || T.outline, 'transparent'),
              border: `1px solid ${typeColors[type] || T.outlineVariant}`,
            }}>
              {type}
            </Typography>
          )}
          <Box sx={{ flex: 1 }} />
          {project?.fields?.GrippID && (
            <StitchIconBtn title="Open in Gripp"
              onClick={async () => {
                // Airtable-GrippID kan een offerte-id zijn — vraag eerst het échte
                // project-id op; zonder project valt de link terug op de offerte.
                let url = null;
                try {
                  const nr = project?.fields?.Projectnummer;
                  const r = nr ? await authFetch(`${AI_ENGINE_URL}/api/v1/gripp/project-id?number=${encodeURIComponent(nr)}`) : null;
                  const d = r?.ok ? await r.json() : null;
                  if (d?.gripp_project_id) url = buildGrippProjectUrl(d.gripp_project_id);
                } catch { /* fallback hieronder */ }
                if (!url) url = buildGrippOfferUrl(project.fields.GrippID);
                if (url) window.open(url, '_blank', 'noopener');
              }}>
              <Icon name="open_in_new" size={20} />
            </StitchIconBtn>
          )}
          <WijzigingslogKnop projectId={id} titel={project?.fields?.Projectnr_en_naam || project?.fields?.Projectnaam || ''}
            renderKnop={(open) => (
              <StitchIconBtn onClick={open} title="Wijzigingslog: wie wijzigde wat, wanneer">
                <Icon name="history" size={20} />
              </StitchIconBtn>
            )} />
          <StitchIconBtn onClick={() => setShowProjectEdit(true)} title="Project bewerken">
            <Icon name="edit" size={20} />
          </StitchIconBtn>
          <StitchIconBtn onClick={() => setShowPlanningEmail(true)} title="Mail planning naar klant">
            <Icon name="mail" size={20} />
          </StitchIconBtn>
          {/* Zichtbare schakelaar tussen de flow en de vertrouwde tabbladen */}
          <Box sx={{ display: 'flex', gap: 0.5, p: 0.5, bgcolor: T.surfaceLow, borderRadius: '9999px' }}>
            {[{ value: 'flow', icon: 'view_timeline', label: 'Flow' }, { value: 'tabs', icon: 'tab', label: 'Tabbladen' }].map((opt) => (
              <Box key={opt.value} onClick={() => kiesWeergave(opt.value)} sx={{
                display: 'flex', alignItems: 'center', gap: 0.5, px: 1.5, py: 0.5,
                borderRadius: '9999px', cursor: 'pointer',
                fontFamily: T.fontBody, fontSize: '0.75rem',
                fontWeight: weergave === opt.value ? 700 : 500,
                color: weergave === opt.value ? '#fff' : T.secondary,
                bgcolor: weergave === opt.value ? T.primary : 'transparent',
                transition: 'all 0.15s ease',
                '&:hover': weergave === opt.value ? {} : { bgcolor: T.surfaceHigh },
              }}>
                <Icon name={opt.icon} size={15} />
                {opt.label}
              </Box>
            ))}
          </Box>
          <StitchIconBtn onClick={loadData} title="Vernieuwen">
            <Icon name="refresh" size={20} />
          </StitchIconBtn>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5, flexWrap: 'wrap', mb: 0.5 }}>
          <Typography variant="h5" sx={{ fontFamily: T.font, fontWeight: 700, color: T.text, letterSpacing: '-0.02em' }}>
            {title}
          </Typography>
          {/* Alleen tonen als de klantnaam niet al in de projecttitel zit — anders dubbelop */}
          {client && !title.toLowerCase().includes(String(client).toLowerCase()) && (
            <Typography variant="body2" sx={{ fontFamily: T.fontBody, color: T.outline }}>
              {client}
            </Typography>
          )}
        </Box>

        {/* Team: per gevulde rol de naam(en), één stille regel */}
        <TeamRegel fields={project?.fields} sx={{ mb: 1.5 }} />

        {/* Quick stats — heel subtiel klikbaar: elke teller opent zijn paneel */}
        <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
          <Box sx={statClickSx} onClick={() => openPaneel('draaidagen')}>
            <Typography sx={{ ...stitchSectionLabel, fontSize: '0.65rem' }}>
              Draaidagen
            </Typography>
            <Typography variant="h6" sx={{ fontFamily: T.font, fontWeight: 600 }}>{draaidagen.length}</Typography>
          </Box>

          <Box sx={{ ...statClickSx, minWidth: 100 }} onClick={() => openPaneel('taken')}>
            <Typography sx={{ ...stitchSectionLabel, fontSize: '0.65rem' }}>
              Taken
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="h6" sx={{ fontFamily: T.font, fontWeight: 600 }}>{completedTasks}/{tasks.length}</Typography>
              <Box sx={{ width: 50 }}>
                <LinearProgress
                  variant="determinate"
                  value={taskProgress}
                  sx={{
                    height: 5,
                    borderRadius: 3,
                    backgroundColor: T.surfaceLow,
                    '& .MuiLinearProgress-bar': { backgroundColor: T.emerald, borderRadius: 3 },
                  }}
                />
              </Box>
            </Box>
          </Box>

          <Box sx={statClickSx} onClick={() => openPaneel('deliverables')}>
            <Typography sx={{ ...stitchSectionLabel, fontSize: '0.65rem' }}>
              Deliverables
            </Typography>
            <Typography variant="h6" sx={{ fontFamily: T.font, fontWeight: 600 }}>{deliverables.length}</Typography>
          </Box>

          <Box sx={statClickSx} onClick={() => openPaneel('quitclaims')}>
            <Typography sx={{ ...stitchSectionLabel, fontSize: '0.65rem' }}>
              Quitclaims
            </Typography>
            <Typography variant="h6" sx={{ fontFamily: T.font, fontWeight: 600 }}>{quitclaims.length}</Typography>
          </Box>

          <Box sx={statClickSx} onClick={() => openPaneel('callsheets')}>
            <Typography sx={{ ...stitchSectionLabel, fontSize: '0.65rem' }}>
              Callsheets
            </Typography>
            <Typography variant="h6" sx={{ fontFamily: T.font, fontWeight: 600 }}>{callsheets.length}</Typography>
          </Box>

          <Box sx={statClickSx} onClick={() => openPaneel('notes')}>
            <Typography sx={{ ...stitchSectionLabel, fontSize: '0.65rem' }}>
              Notes
            </Typography>
            <Typography variant="h6" sx={{ fontFamily: T.font, fontWeight: 600 }}>{notes.length}</Typography>
          </Box>

          <Box sx={statClickSx} onClick={() => openPaneel('meetings')}>
            <Typography sx={{ ...stitchSectionLabel, fontSize: '0.65rem' }}>
              Meetings
            </Typography>
            <Typography variant="h6" sx={{ fontFamily: T.font, fontWeight: 600 }}>{transcripts.length}</Typography>
          </Box>

          <Box sx={statClickSx} onClick={() => openPaneel('documenten')}>
            <Typography sx={{ ...stitchSectionLabel, fontSize: '0.65rem' }}>
              Documenten
            </Typography>
            {docCount === null
              ? <Icon name="folder_open" size={22} sx={{ color: T.outline, mt: 0.4 }} />
              : <Typography variant="h6" sx={{ fontFamily: T.font, fontWeight: 600 }}>{docCount}</Typography>}
          </Box>

          <Box sx={statClickSx} onClick={() => openPaneel('correspondentie')}>
            <Typography sx={{ ...stitchSectionLabel, fontSize: '0.65rem' }}>
              Correspondentie
            </Typography>
            {mailCount === null
              ? <Icon name="forum" size={22} sx={{ color: T.outline, mt: 0.4 }} />
              : <Typography variant="h6" sx={{ fontFamily: T.font, fontWeight: 600 }}>{mailCount}</Typography>}
          </Box>
        </Box>
      </Box>

      {/* Klaargezet taakvoorstel na acceptatie (draft-only) */}
      {pendingProposal && (
        <Box sx={{
          display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap',
          p: 2, mb: 2, borderRadius: '0.75rem',
          bgcolor: T.tertiaryContainer + '55', border: `1px solid ${T.tertiary}55`,
        }}>
          <Icon name="checklist" size={22} sx={{ color: T.tertiary }} />
          <Box sx={{ flex: 1, minWidth: 200 }}>
            <Typography sx={{ fontFamily: T.font, fontWeight: 700, color: T.text, fontSize: '0.9rem' }}>
              Taakvoorstel klaar sinds acceptatie
            </Typography>
            <Typography variant="caption" sx={{ fontFamily: T.fontBody, color: T.secondary }}>
              {pendingProposal.task_count} actiepunten voorgesteld uit de offerte — nog niet ingepland. Bekijk, pas aan en maak aan.
            </Typography>
          </Box>
          <Box component="span" onClick={() => { markProposal('applied'); setShowSmartTasks(true); }}
            sx={{ ...stitchGradientBtn, fontSize: '0.8rem' }}>
            <Icon name="auto_awesome" size={16} /> Openen
          </Box>
          <Box component="span" onClick={() => markProposal('dismissed')} sx={{ ...stitchOutlineBtn, fontSize: '0.8rem' }}>
            Later
          </Box>
        </Box>
      )}

      {/* Klantvereisten (bv. Schneider): rustig blok, alleen als het project bij een klantgroep hoort. */}
      <KlantVereistenBlok projectId={id} nummer={project?.fields?.Projectnummer} variant="project" />

      {/* Flow-weergave (standaard) — de fasenbalk als startpagina */}
      {weergave === 'flow' && (
        <ProjectFlowPage onOpenPaneel={openPaneel}
          onOpenDialog={(key) => {
            if (key === 'smartTasks') setShowSmartTasks(true);
            else if (key === 'preproWizard') setShowPreproWizard(true);
          }} />
      )}

      {/* Oude tabbladweergave via ?weergave=tabs */}
      {weergave === 'tabs' && (
      <>
      {/* Tab bar */}
      <Box sx={{ display: 'flex', gap: 0, overflowX: 'auto', borderBottom: `1px solid ${T.surfaceLow}`, mt: 1, mb: 2,
        '&::-webkit-scrollbar': { height: 0 },
      }}>
        {tabDefs.map((tab) => (
          <Box
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.6,
              px: 1.75,
              py: 1.25,
              cursor: 'pointer',
              fontFamily: T.fontBody,
              fontSize: '0.78rem',
              fontWeight: activeTab === tab.key ? 700 : 500,
              color: activeTab === tab.key ? T.tertiary : T.outlineVariant,
              borderBottom: activeTab === tab.key ? `2px solid ${T.tertiary}` : '2px solid transparent',
              transition: 'all 0.12s ease',
              whiteSpace: 'nowrap',
              flexShrink: 0,
              '&:hover': activeTab === tab.key ? {} : { color: T.secondary, borderBottomColor: `${T.outlineVariant}40` },
            }}
          >
            <Icon name={tab.icon} size={16} sx={{ color: activeTab === tab.key ? T.tertiary : T.outlineVariant }} />
            {tab.label}
          </Box>
        ))}
      </Box>

      {/* Activity Feed Tab */}
      <TabPanel value={activeTab} index="activiteit">
        <ActivityPanel onOpenTab={setActiveTab} />
      </TabPanel>

      {/* Tasks Tab */}
      <TabPanel value={activeTab} index="taken">
        <TasksPanel
          tasksView={tasksView}
          setTasksView={setTasksView}
          kanbanShowPLOnly={kanbanShowPLOnly}
          setKanbanShowPLOnly={setKanbanShowPLOnly}
          onOpenSmartTasks={() => setShowSmartTasks(true)}
          onOpenPreproWizard={() => setShowPreproWizard(true)}
        />
      </TabPanel>

      {/* Deliverables Tab */}
      <TabPanel value={activeTab} index="deliverables">
        <DeliverablesPanel
          onOpenDetail={(item) => { setSelectedDeliverable(item); setShowDeliverableDetail(true); }}
          onGenerateTasks={(deliverable) => { setSelectedDeliverable(deliverable); setShowTaskGenerator(true); }}
        />
      </TabPanel>

      {/* Draaidagen Tab */}
      <TabPanel value={activeTab} index="draaidagen">
        <DraaidagenPanel draaidagenView={draaidagenView} setDraaidagenView={setDraaidagenView} />
      </TabPanel>

      {/* Quitclaims Tab */}
      <TabPanel value={activeTab} index="quitclaims">
        <QuitclaimsPanel />
      </TabPanel>

      {/* Callsheets Tab */}
      <TabPanel value={activeTab} index="callsheets">
        <CallsheetsPanel />
      </TabPanel>

      {/* Notes Tab */}
      <TabPanel value={activeTab} index="notes">
        <NotesPanel />
      </TabPanel>

      {/* Meetings Tab */}
      <TabPanel value={activeTab} index="meetings">
        <MeetingsPanel />
      </TabPanel>

      {/* Frame.io Videos Tab */}
      {frameioVideos.length > 0 && (
        <TabPanel value={activeTab} index="frameio">
          <FrameioPanel />
        </TabPanel>
      )}

      {/* QA Reviews Tab */}
      <TabPanel value={activeTab} index="qa">
        <QaPanel />
      </TabPanel>

      {/* Correspondentie Tab — alle klant-mails rond dit project */}
      <TabPanel value={activeTab} index="correspondentie">
        <CorrespondencePanel active={activeTab === 'correspondentie'} />
      </TabPanel>

      {/* Offerteregels Tab — wat er verkocht is, per sectie */}
      <TabPanel value={activeTab} index="offerteregels">
        <OfferteregelsPanel />
      </TabPanel>

      {/* Script uit SharePoint (alleen lezen; bewerken in Word) */}
      <TabPanel value={activeTab} index="script">
        <ScriptPanel />
      </TabPanel>

      </>
      )}

      {/* Elk paneel als drawer vanuit de flow (?paneel=<key>) */}
      <Drawer anchor="right" open={!!paneel} onClose={closePaneel}
        PaperProps={{ sx: { width: { xs: '100vw', md: 900 }, maxWidth: '96vw', p: { xs: 2, md: 3 } } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <Typography sx={{ fontFamily: T.font, fontWeight: 800, fontSize: '1.05rem', flex: 1 }}>
            {PANEL_LABELS[paneel] || paneel}
          </Typography>
          <StitchIconBtn onClick={closePaneel} title="Sluiten">
            <Icon name="close" size={20} />
          </StitchIconBtn>
        </Box>
        {paneel && renderPaneel(paneel)}
      </Drawer>

      {/* Task Detail Dialog */}
      <TaskDetailDialog
        task={selectedTask}
        open={!!selectedTask}
        onClose={() => { setSelectedTask(null); setAutoEditTaskDate(false); }}
        onUpdate={handleTaskUpdate}
        onDelete={handleTaskDelete}
        autoEditDate={autoEditTaskDate}
        personen={personen}
      />

      {/* Task Template Generator Dialog */}
      <TaskTemplateGenerator
        open={showTaskGenerator}
        onClose={() => {
          setShowTaskGenerator(false);
          setSelectedDeliverable(null);
        }}
        deliverable={selectedDeliverable}
        alleDeliverables={deliverables}
        projectId={id}
        project={project}
        onSuccess={() => {
          // Refresh data after tasks are generated
          loadData();
          // NB: schakelde historisch naar de Activiteit-tab (index 0), al zegt de
          // oude comment 'Tasks tab' — gedrag bewust behouden bij de key-migratie.
          setActiveTab('activiteit');
          setTasksView('timeline');
        }}
      />

      {/* Slimme taken op basis van de offerte (lokale offerteregels via de engine) */}
      <SmartTaskGenerator
        open={showSmartTasks}
        onClose={() => setShowSmartTasks(false)}
        projectId={project?.airtable_id || project?.id}
        projectName={project?.fields?.Projectnaam || project?.name || ''}
        onCreated={() => {
          loadData();
          setActiveTab('taken');
        }}
      />

      {/* Preproduction Wizard */}
      <PreproductionWizard
        open={showPreproWizard}
        onClose={() => {
          setShowPreproWizard(false);
          loadData();
        }}
        project={project}
        onOpenTemplateManager={() => {
          setShowPreproWizard(false);
          setShowTemplateManager(true);
        }}
      />

      {/* Workflow Template Manager */}
      <WorkflowTemplateManager
        open={showTemplateManager}
        onClose={() => setShowTemplateManager(false)}
      />

      {/* Deliverable Detail Dialog */}
      <DeliverableDetailDialog
        open={showDeliverableDetail}
        onClose={() => {
          setShowDeliverableDetail(false);
          setSelectedDeliverable(null);
        }}
        deliverable={selectedDeliverable}
        taskCount={selectedDeliverable ? (tasksPerDeliverable[selectedDeliverable.id] || 0) : 0}
        onGenerateTasks={(deliverable) => {
          setShowDeliverableDetail(false);
          setSelectedDeliverable(deliverable);
          setShowTaskGenerator(true);
        }}
        onUpdate={(updatedDeliverable) => {
          // Update the deliverables list with the updated data
          setDeliverables(prev => prev.map(d =>
            d.id === updatedDeliverable.id ? updatedDeliverable : d
          ));
          setSelectedDeliverable(updatedDeliverable);
        }}
      />

      {/* Planning Email Dialog */}
      {showPlanningEmail && (
        <PlanningEmailDialog
          open={showPlanningEmail}
          onClose={() => setShowPlanningEmail(false)}
          project={project}
          deliverables={deliverables}
          tasks={tasks}
          msalInstance={instance}
          account={accounts?.[0]}
          senderName={accounts?.[0]?.name || ''}
        />
      )}

      {/* Project Edit Dialog */}
      <ProjectEditDialog
        open={showProjectEdit}
        onClose={() => setShowProjectEdit(false)}
        project={project}
        notes={notes}
        deliverables={deliverables}
        tasks={tasks}
        draaidagen={draaidagen}
        onVeldBijgewerkt={setProject}
        onDeliverableUpdate={(bijgewerkt) => setDeliverables(prev => prev.map(d => (d.id === bijgewerkt.id ? bijgewerkt : d)))}
        onUpdate={(updatedProject) => {
          setProject(updatedProject);
          setShowProjectEdit(false);
        }}
        onNotesUpdate={loadData}
      />
    </Box>
  );
}

export default ProjectOverview;
